import { clsx } from 'clsx'
import { ReactNode, useEffect, useMemo, useRef } from 'react'

export type HighlightMap = {
  [key: string]: string
}

type Props = {
  className?: string
  placeholder?: string
  value?: string
  onChange?: (v: string) => void
  highlights: HighlightMap
}
export function HighlightableTextArea({
  className,
  value = '',
  onChange,
  placeholder,
  highlights = {}
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)

  function handleScroll(): void {
    // We need to sync the scroll positions so that the highlights line up with the text underneath
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft
    }
  }

  useEffect(() => {
    handleScroll()
  }, [value])

  return (
    <div className="relative mx-auto h-[500px] w-full max-w-2xl">
      <div
        ref={backdropRef}
        className="pointer-events-none absolute inset-0 overflow-auto rounded-md border-0 border-transparent bg-white ring-gray-300"
      >
        <HighlightOverlay value={value} highlights={highlights} />
      </div>
      <textarea
        ref={textareaRef}
        placeholder={placeholder}
        className={clsx(
          'absolute inset-0 resize-none overflow-auto rounded-md border-0 bg-transparent p-4 text-lg leading-relaxed text-gray-700 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400',
          className
        )}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onScroll={handleScroll}
      />
    </div>
  )
}

function HighlightOverlay({ value, highlights }: { value: string; highlights: HighlightMap }) {
  const segments = useMemo(() => buildHighlightSegments(value, highlights), [value, highlights])

  return (
    <div className="whitespace-pre-wrap break-words p-4 text-lg leading-relaxed text-transparent">
      {segments.map((segment, idx) =>
        segment.className ? (
          <mark key={idx} className={clsx(segment.className, 'rounded')}>
            {segment.text}
          </mark>
        ) : (
          <span key={idx}>{segment.text}</span>
        )
      )}
    </div>
  )
}

export function HighlightChip({ className, children }: { className: string; children: ReactNode }) {
  return (
    <mark className={clsx('px-0 py-0 text-lg leading-relaxed text-gray-700', className)}>
      {children}
    </mark>
  )
}

type HighlightSegment = {
  text: string
  className?: string
}

function buildHighlightSegments(input: string, highlightConfig: HighlightMap): HighlightSegment[] {
  if (!input) {
    return [{ text: '' }]
  }

  let segments: HighlightSegment[] = [{ text: input }]

  Object.entries(highlightConfig).forEach(([pattern, className]) => {
    const regex = new RegExp(`${escapeRegExp(pattern)}\\w*`, 'g')

    segments = segments.flatMap((segment) => {
      if (segment.className) {
        return [segment]
      }

      const parts: HighlightSegment[] = []
      let lastIndex = 0
      let match: RegExpExecArray | null = null
      regex.lastIndex = 0

      while ((match = regex.exec(segment.text)) !== null) {
        const start = match.index ?? 0
        const end = start + match[0].length

        if (start > lastIndex) {
          parts.push({ text: segment.text.slice(lastIndex, start) })
        }

        parts.push({
          text: segment.text.slice(start, end),
          className
        })

        lastIndex = end
      }

      if (!parts.length) {
        return [segment]
      }

      if (lastIndex < segment.text.length) {
        parts.push({ text: segment.text.slice(lastIndex) })
      }

      return parts
    })
  })

  if (input.endsWith('\n')) {
    segments = [...segments, { text: '\n' }]
  }

  return segments
}

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
